import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/DialogSheet';
import ProccessSpinner from '@/components/ProcessSpinner';
import { REVIEW_URL } from '@/config';
import { useSubscribed, useUser } from '@/hooks/user';
import ImageRateUs from '@/icons/rate-us.svg';
import Success from '@/icons/success.svg';
import ThumbsUp from '@/icons/thumbs-up.svg';
import { setTriggerAction } from '@/store/editor';
import { setIsModalRateUsOpen } from '@/store/myDocuments';
import { updateUser } from '@/store/profile/thunks';
import { delay, hasPassedDays, showError } from '@/utils';

function RateUs() {
	const SCREENS = { START: 'start', LOADING: 'loading', END: 'end' };
	const [currentScreen, setCurrentScreen] = useState(SCREENS.START);
	const user = useUser();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const isEmpty = useSelector(state => state.myDocuments.isEmpty);
	const isLessThanSevenDays = !hasPassedDays(user.createdAt, 7);
	const isSubscribed = useSubscribed();
	const isVisible = !isEmpty && isSubscribed && isLessThanSevenDays;
	const open = useSelector(state => state.myDocuments.isModalRateUsOpen);

	const handleClick = useCallback(
		async function () {
			try {
				await dispatch(updateUser({ reviewRequested: true }));
				setCurrentScreen(SCREENS.LOADING);
				await delay(30_000);
				setCurrentScreen(SCREENS.END);
			} catch (reason) {
				showError(reason);
			}
		},
		[dispatch]
	);

	const handleOpenChange = payload => {
		dispatch(setIsModalRateUsOpen(payload));
	};

	const handleClose = () => {
		dispatch(setIsModalRateUsOpen(false));
		dispatch(setTriggerAction('download'));
	};

	if (!isVisible) {
		return null;
	}

	const screenStart = (
		<>
			<div className="p-4">
				<div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-light-red px-4 pt-6 sm:flex-row sm:items-end sm:items-center sm:pt-0">
					<div className="flex flex-col items-start gap-2 sm:py-6">
						<ThumbsUp aria-hidden className="size-6 text-red" />
						<p className="mb-1 text-xs font-semibold leading-5">
							{t('myDocuments.rateUs.start.title')}
						</p>
						<p className="text-xs leading-5">
							{t('myDocuments.rateUs.start.description')}
						</p>
					</div>
					<Button
						aria-label={t('myDocuments.rateUs.start.button')}
						className="h-full p-0"
						Component="a"
						href={REVIEW_URL}
						onClick={handleClick}
						target="_blank"
						variant="ghost"
					>
						<ImageRateUs title={t('myDocuments.rateUs.start.title')} />
					</Button>
				</div>
			</div>
			<DialogFooter className="grid-cols-1">
				<Button
					Component="a"
					href={REVIEW_URL}
					onClick={handleClick}
					target="_blank"
				>
					{t('myDocuments.rateUs.start.button')}
				</Button>
			</DialogFooter>
		</>
	);

	const screenLoading = (
		<div className="flex flex-col items-center gap-4 px-4 py-8 sm:gap-5 sm:px-6">
			<ProccessSpinner aria-hidden className="size-10" />
			<div className="text-center">
				<p className="mb-1 text-sm font-semibold leading-6 text-black sm:text-base sm:leading-7">
					{t('myDocuments.rateUs.loading.title')}
				</p>
				<p className="text-xs leading-5 text-grey-500 sm:text-base sm:leading-7">
					{t('myDocuments.rateUs.loading.description')}
				</p>
			</div>
		</div>
	);

	const screenEnd = (
		<>
			<div className="flex flex-col items-center px-4 py-8 sm:px-6">
				<Success aria-hidden className="size-15" />
				<div className="text-center">
					<p className="text-sm font-semibold leading-6 text-black sm:text-base sm:leading-7">
						{t('myDocuments.rateUs.end.title')}
					</p>
					<p className="text-xs leading-5 text-grey-500 sm:text-base sm:leading-7">
						{t('myDocuments.rateUs.end.description')}
					</p>
				</div>
			</div>
			<DialogFooter className="grid-cols-1">
				<Button onClick={handleClose}>{t('common.close')}</Button>
			</DialogFooter>
		</>
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className="w-full sm:max-w-[397px]"
				hasCross={false}
				onOpenAutoFocus={e => e.preventDefault()}
				onInteractOutside={ev => ev.preventDefault()}
			>
				<DialogTop title={t('myDocuments.rateUs.title')} />
				{currentScreen === SCREENS.START && screenStart}
				{currentScreen === SCREENS.LOADING && screenLoading}
				{currentScreen === SCREENS.END && screenEnd}
			</DialogContent>
		</Dialog>
	);
}

export default RateUs;
